/**
 * ACTIVITY-CORE (L2) — Workspace Trash: soft-deleted time entries and
 * projects. Restoring brings them back to live view (and timesheet
 * balances again); "Delete forever" physically purges them (irreversible). Timesheet
 * and reports EXCLUDE deleted items and balance upon restore.
 */
import { createRoute } from "@tanstack/solid-router";
import { createSignal, For, Show } from "solid-js";
import { ArchiveRestore, Folder, Timer, Trash2 } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { chronosRoute } from "../chronos";
import { GlassSurface, PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { showToast } from "@/components/ui/toast";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useWorkspace } from "@/features/chronos-sync/workspace-context";
import { createPowerSyncQuery } from "@time-tracker/shared/solid";
import { patchRowVerified, queryKeys, TRASH_PURGE_DAYS } from "@time-tracker/shared";
import {
  and,
  desc,
  eq,
  isNotNull,
  project,
  type Project,
  time_entry,
  type TimeEntry,
} from "@time-tracker/powersync";

const TRASH_LIMIT = 200;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * "will be permanently deleted in N days" — countdown = deleted_at +
 * TRASH_PURGE_DAYS − now. Returns the remaining days (minimum 0) or null if
 * the job is disabled (`TRASH_PURGE_DAYS <= 0`) or the date is invalid.
 */
function daysUntilPurge(iso: string | null): number | null {
  if (TRASH_PURGE_DAYS <= 0 || !iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const purgeAt = date.getTime() + TRASH_PURGE_DAYS * MS_PER_DAY;
  return Math.max(0, Math.ceil((purgeAt - Date.now()) / MS_PER_DAY));
}

function ChronosTrashPage() {
  const { t, locale } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const { activeWorkspaceId } = useWorkspace();

  const [purgeEntry, setPurgeEntry] = createSignal<TimeEntry | null>(null);
  const [purgeProject, setPurgeProject] = createSignal<Project | null>(null);

  // Time entries in trash (mine in the active workspace).
  const trashedEntries = createPowerSyncQuery<TimeEntry>({
    queryKey: () => queryKeys.activity.trashEntries(userId(), activeWorkspaceId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(time_entry)
        .where(and(eq(time_entry.user_id, userId()), isNotNull(time_entry.deleted_at)))
        .orderBy(desc(time_entry.deleted_at))
        .limit(TRASH_LIMIT);
    },
    enabled: () => Boolean(drizzle()) && Boolean(userId()),
    suspense: false,
  });

  // Projects in trash for the active workspace.
  const trashedProjects = createPowerSyncQuery<Project>({
    queryKey: () => queryKeys.activity.trashProjects(userId(), activeWorkspaceId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(project)
        .where(and(eq(project.workspace_id, activeWorkspaceId()), isNotNull(project.deleted_at)))
        .orderBy(desc(project.deleted_at))
        .limit(TRASH_LIMIT);
    },
    enabled: () => Boolean(drizzle()) && Boolean(activeWorkspaceId()),
    suspense: false,
  });

  const restoreEntry = async (entry: TimeEntry) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await patchRowVerified(dbInstance, time_entry, time_entry.id, entry.id, {
        deleted_at: null,
      });
      showToast(t(msg`Time block restored`), "success");
    } catch (err) {
      console.error("[trash] restore entry failed:", err);
      showToast(t(msg`Could not restore the time block`), "error");
    }
  };

  const restoreProject = async (proj: Project) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await patchRowVerified(dbInstance, project, project.id, proj.id, { deleted_at: null });
      showToast(t(msg`Project restored`), "success");
    } catch (err) {
      console.error("[trash] restore project failed:", err);
      showToast(t(msg`Could not restore the project`), "error");
    }
  };

  const confirmPurgeEntry = async () => {
    const entry = purgeEntry();
    const dbInstance = drizzle();
    if (!entry || !dbInstance) {
      return;
    }
    try {
      await dbInstance.delete(time_entry).where(eq(time_entry.id, entry.id));
      showToast(t(msg`Time block permanently deleted`), "success");
    } catch (err) {
      console.error("[trash] purge entry failed:", err);
      showToast(t(msg`Could not delete the time block`), "error");
    } finally {
      setPurgeEntry(null);
    }
  };

  const confirmPurgeProject = async () => {
    const proj = purgeProject();
    const dbInstance = drizzle();
    if (!proj || !dbInstance) {
      return;
    }
    try {
      await dbInstance.delete(project).where(eq(project.id, proj.id));
      showToast(t(msg`Project permanently deleted`), "success");
    } catch (err) {
      console.error("[trash] purge project failed:", err);
      showToast(t(msg`Could not delete the project`), "error");
    } finally {
      setPurgeProject(null);
    }
  };

  const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(locale()) : "");

  const entries = () => trashedEntries.data ?? [];
  const projects = () => trashedProjects.data ?? [];
  const isEmpty = () => entries().length === 0 && projects().length === 0;

  return (
    <PageContainer maxWidth="3xl">
      <PageHeader
        title={t(msg`Trash`)}
        description={t(msg`Deleted time blocks and projects. Restore them or delete forever.`)}
        backTo="/chronos/settings"
        icon={Trash2}
      />

      <div class="mt-6 space-y-8" data-testid="trash-view">
        <Show
          when={!isEmpty()}
          fallback={
            <EmptyState
              icon={Trash2}
              title={t(msg`Trash is empty`)}
              description={t(msg`Deleted time blocks and projects will appear here.`)}
            />
          }
        >
          {/* ── Bloques de tiempo ─────────────────────────────────────────── */}
          <Show when={entries().length > 0}>
            <section data-testid="trash-time-entries">
              <h2 class="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                <Timer class="h-4 w-4" />
                {t(msg`Time blocks`)}
              </h2>
              <div class="space-y-2">
                <For each={entries()}>
                  {(entry) => (
                    <GlassSurface class="flex items-center justify-between gap-3 rounded-xl p-3">
                      <div class="min-w-0">
                        <p class="truncate text-sm font-medium">
                          {entry.description?.trim() || t(msg`(no description)`)}
                        </p>
                        <p class="text-xs text-muted-foreground">
                          {t(msg`Deleted {date}`, { date: fmtDate(entry.deleted_at) })}
                        </p>
                        <Show when={daysUntilPurge(entry.deleted_at) !== null}>
                          <p
                            class="text-xs text-muted-foreground/80"
                            data-testid="trash-item-purge-countdown"
                          >
                            {t(msg`Will be permanently deleted in {days} days`, {
                              days: daysUntilPurge(entry.deleted_at) ?? 0,
                            })}
                          </p>
                        </Show>
                      </div>
                      <div class="flex shrink-0 items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void restoreEntry(entry)}
                          data-testid="trash-restore"
                        >
                          <ArchiveRestore class="mr-1 h-4 w-4" />
                          {t(msg`Restore`)}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPurgeEntry(entry)}
                          data-testid="trash-purge"
                        >
                          <Trash2 class="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </GlassSurface>
                  )}
                </For>
              </div>
            </section>
          </Show>

          {/* ── Proyectos ─────────────────────────────────────────────────── */}
          <Show when={projects().length > 0}>
            <section data-testid="trash-projects">
              <h2 class="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                <Folder class="h-4 w-4" />
                {t(msg`Projects`)}
              </h2>
              <div class="space-y-2">
                <For each={projects()}>
                  {(proj) => (
                    <GlassSurface class="flex items-center justify-between gap-3 rounded-xl p-3">
                      <div class="min-w-0">
                        <p class="truncate text-sm font-medium">{proj.name}</p>
                        <p class="text-xs text-muted-foreground">
                          {t(
                            msg`Deleted {date}. Its time blocks are kept and reappear on restore.`,
                            {
                              date: fmtDate(proj.deleted_at),
                            },
                          )}
                        </p>
                        <Show when={daysUntilPurge(proj.deleted_at) !== null}>
                          <p
                            class="text-xs text-muted-foreground/80"
                            data-testid="trash-item-purge-countdown"
                          >
                            {t(msg`Will be permanently deleted in {days} days`, {
                              days: daysUntilPurge(proj.deleted_at) ?? 0,
                            })}
                          </p>
                        </Show>
                      </div>
                      <div class="flex shrink-0 items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void restoreProject(proj)}
                          data-testid="trash-restore"
                        >
                          <ArchiveRestore class="mr-1 h-4 w-4" />
                          {t(msg`Restore`)}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPurgeProject(proj)}
                          data-testid="trash-purge"
                        >
                          <Trash2 class="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </GlassSurface>
                  )}
                </For>
              </div>
            </section>
          </Show>
        </Show>
      </div>

      <ConfirmDialog
        open={purgeEntry() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPurgeEntry(null);
          }
        }}
        variant="destructive"
        title={t(msg`Delete time block forever?`)}
        description={t(msg`This permanently deletes the time block. This cannot be undone.`)}
        confirmLabel={t(msg`Delete forever`)}
        onConfirm={() => void confirmPurgeEntry()}
      />
      <ConfirmDialog
        open={purgeProject() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPurgeProject(null);
          }
        }}
        variant="destructive"
        title={t(msg`Delete project forever?`)}
        description={t(
          msg`This permanently deletes the project and all its time blocks and tasks. This cannot be undone.`,
        )}
        confirmLabel={t(msg`Delete forever`)}
        onConfirm={() => void confirmPurgeProject()}
      />
    </PageContainer>
  );
}

export const chronosTrashRoute = createRoute({
  getParentRoute: () => chronosRoute,
  path: "/trash",
  component: ChronosTrashPage,
});
